import { inject, Injectable } from "@angular/core";
import { LogAccess, LogLevel } from "@tylertech/logging";
import { Utilities } from "@tylertech/shared-module";
import { CopyProperty, IGridViewMetadata, IViewMetadata } from "@tylertech/tyler-api-types";
import { Observable, Subject } from "rxjs";
import { RepositoryFormData } from "../interfaces";
import { ProviderStorageService } from "../services/table/provider.storage.service";
import { ObjectHelpers } from "../utilities";
import { GridEventService } from "./table/gridEvent.service";
import { NavigationService } from "@tylertech/core-module";
import { ResetStateService } from "./reset.state.service";

export interface EditNextParams
{
    index: number;
    selectedViewPath?: string;
}

@Injectable({ providedIn: "root" })
export class MultipleEntityEditService extends ResetStateService
{
    protected _providers: string[] = [];
    protected _currentEditIndices: Record<string, number> = {};
    protected _multipleEditTotals: Record<string, number> = {};
    protected _copyProperties: Record<string, CopyProperty[]> = {};

    IsEditNext: boolean;
    DelayNavigate: number = 0;
    MultipleEditLoading: boolean = false;
    DataController: RepositoryFormData;
    CopyEntity: any;
    CopyAll: boolean;
    DoCopy: boolean = false;
    IsMultipleEditing: boolean;
    orderedIdsWithFilter: [string, boolean][];
    editActionEntity: any;

    constructor(
        private $gridEventService: GridEventService,
        private $providerStorageService: ProviderStorageService
    )
    {
        super(inject(NavigationService));
    }

    get ProviderId(): string
    {
        return this._providers?.slice(-1)[0];
    }

    set ProviderId(value: string)
    {
        if (this._providers.indexOf(value) === -1)
            this._providers.push(value);
    }


    @LogAccess({ level: LogLevel.DEBUG, logGet: false })
    get CurrentEditIndex(): number
    {
        return this._currentEditIndices[this.ProviderId];
    }

    set CurrentEditIndex(value: number)
    {
        this._currentEditIndices[this.ProviderId] = value;
    }

    private _editableEntities: Record<string, any[]> = {};

    get EditableEntities(): any[]
    {
        return this._editableEntities[this.ProviderId];
    }

    set EditableEntities(value: any[])
    {
        this._editableEntities[this.ProviderId] = value;
    }

    private _editFormSelectedViewPath: string;

    @LogAccess({ level: LogLevel.DEBUG, logGet: false })
    get editFormSelectedViewPath(): string { return this._editFormSelectedViewPath; }

    set editFormSelectedViewPath(value: string)
    {
        this._editFormSelectedViewPath = value;
    }


    get MultipleEditTotal(): number
    {
        return this._multipleEditTotals[this.ProviderId];
    }

    set MultipleEditTotal(value: number)
    {
        this._multipleEditTotals[this.ProviderId] = value;
    }


    get CopyProperties(): CopyProperty[]
    {
        return this._copyProperties[this.ProviderId];
    }

    set CopyProperties(value: CopyProperty[])
    {
        this._copyProperties[this.ProviderId] = value;
    }

    editNextProviders: string[] = [];
    addNewProviders: string[] = [];
    addAndCopyProviders: string[] = [];

    private readonly editNextSubject$ = new Subject<EditNextParams>();
    private readonly addNewSubject$ = new Subject<void>();
    private readonly addAndCopySubject$ = new Subject<any>();
    private readonly addNewSeparateFormSubject$ = new Subject<void>();

    get onEditNext(): Observable<EditNextParams>
    {
        return this.editNextSubject$.asObservable();
    }

    get onAddNew(): Observable<void>
    {
        return this.addNewSubject$.asObservable();
    }

    get onAddAndCopy(): Observable<any>
    {
        return this.addAndCopySubject$.asObservable();
    }

    get onAddNewSeparateForm(): Observable<any>
    {
        return this.addNewSeparateFormSubject$.asObservable();
    }

    fireEditNextListeners(index: number): void
    {
        this.editNextSubject$.next({ index, selectedViewPath: this.editFormSelectedViewPath });
    }

    fireAddNewListener(): void
    {
        this.addNewSubject$.next();
    }

    fireAddAndCopyListener(entity: any): void
    {
        this.addAndCopySubject$.next(entity);
    }

    hasEditNextListener(providerId: string): boolean
    {
        return this.editNextProviders.indexOf(providerId) > -1;
    }

    hasAddNewListener(providerId?: string): boolean
    {
        providerId = Utilities.IsNullOrUndefined(providerId) ? this.ProviderId : providerId;
        return this.addNewProviders.indexOf(providerId) > -1;
    }

    hasAddAndCopyListener(providerId?: string): boolean
    {
        providerId = Utilities.IsNullOrUndefined(providerId) ? this.ProviderId : providerId;
        return this.addAndCopyProviders.indexOf(providerId) > -1;
    }

    shouldInitiateMultipleEdit(view: IViewMetadata): boolean
    {
        if (view.EditAction)
            return false;
        if (view.AddAction && view.AddAction.ViewToShow)
            return false;
        return true;
    }

    endEdit(addMode: boolean): void
    {
        if (!addMode)
        {
            this.EditableEntities?.forEach(x => x["$selectionEdit"] = undefined);
            delete this._currentEditIndices[this.ProviderId];
            delete this._editableEntities[this.ProviderId];
            delete this._multipleEditTotals[this.ProviderId];
            if (this.editNextProviders.indexOf(this.ProviderId) > -1)
                this.editNextProviders.splice(this.editNextProviders.indexOf(this.ProviderId), 1);
        }
        if (this.addNewProviders.indexOf(this.ProviderId) > -1)
            this.addNewProviders.splice(this.addNewProviders.indexOf(this.ProviderId), 1);
        if (this.addAndCopyProviders.indexOf(this.ProviderId) > -1)
            this.addAndCopyProviders.splice(this.addAndCopyProviders.indexOf(this.ProviderId), 1);
        this.CopyProperties = undefined;
        this.CopyAll = false;
        this.DoCopy = false;
        this.DataController = undefined;

        this.$gridEventService.fireUpdateGridSelectionListeners(
            {
                ProviderId: this.ProviderId,
                Clear: true
            }
        );
        this._providers.pop();
    }

    clearEditing(): void
    {
        this._currentEditIndices = {};
        this._multipleEditTotals = {};

        this.editNextProviders = [];
        this.addNewProviders = [];
        this.addAndCopyProviders = [];

        this._copyProperties = {};
        this.CopyAll = false;
        this.DoCopy = false;
        this.DataController = undefined;
        this._providers = [];
        this.orderedIdsWithFilter = undefined;
    }

    maybeEditNext()
    {
        if (!this.IsEditNext)
            return;
        if (this.DelayNavigate > 0)
        {
            this.DelayNavigate--;
            if (this.DelayNavigate === 0)
            {
                this.IsEditNext = false;
                this.editNext();
            }
        }
    }

    setFilteredOrder(view: IGridViewMetadata, fullList: any[]): void
    {
        if (view.SubPropertyName)
            this.orderedIdsWithFilter = fullList.map((m, i) => [ObjectHelpers.GetId(view.GridProperties.IdProperty, m, i), m["$inFilter"]]);
    }

    updateFilteredEditableEntities(view: IGridViewMetadata): void
    {
        if (!view.SubPropertyName || !this.orderedIdsWithFilter)
            return;

        const self = this;
        const filteredIds = this.orderedIdsWithFilter.map(x => x[0]);

        this.$providerStorageService.LocalEntitiesStore[view.GridProperties["$providerId"]].forEach(x =>
        {
            const id = ObjectHelpers.GetId(view.GridProperties.IdProperty, x, 0);
            const match = self.orderedIdsWithFilter.find(f => f[0] === id);
            x["$inFilter"] = match ? match[1] : false;
        });

        this.EditableEntities = this.$providerStorageService.LocalEntitiesStore[view.GridProperties["$providerId"]].filter(x => x["$inFilter"]);

        if (this.EditableEntities.some(x => x["$selectionEdit"]))
            this.EditableEntities = this.EditableEntities.filter(x => x["$selectionEdit"]);

        this.EditableEntities.sort(function (a, b)
        {
            return filteredIds.indexOf(ObjectHelpers.GetId(view.GridProperties.IdProperty, a, 0)) - filteredIds.indexOf(ObjectHelpers.GetId(view.GridProperties.IdProperty, b, 0));
        });
    }

    editNext(): void
    {
        const self = this;
        setTimeout(() => self.fireEditNextListeners(self.CurrentEditIndex), 50);
    }

    setEditingFlag(entity: any): void
    {
        this.EditableEntities?.forEach(e =>
        {
            if (e !== entity)
                delete e["$$Editing"];
        });
        if (entity)
            entity["$$Editing"] = true;
    }

    maybeAddNew(): void
    {
        if (this.IsEditNext)
            return;
        if (this.DelayNavigate > 0)
        {
            this.DelayNavigate--;
            if (this.DelayNavigate === 0)
                this.addNew(false);
        }
    }

    addNew(separateEditForm: boolean): void
    {

        if (separateEditForm)
            this.addNewSeparateFormSubject$.next();
        else
        {
            const self = this;
            requestAnimationFrame(function ()
            {
                if (self.CopyAll)
                    self.fireAddAndCopyListener(self.CopyEntity);
                else if (Utilities.IsNullOrUndefined(self.DataController) || !Utilities.IsNullOrUndefined(self.CopyProperties))
                    self.fireAddNewListener();
            });
        }
    }

    protected override reInitialize(): void
    {
        this.DataController = undefined;
        this.orderedIdsWithFilter = undefined;
    }
}
